import type {Validator} from 'svelte-forms';
import type {AnyObject} from 'typings/portaro.fe.types';
import type {Readable} from 'svelte/store';
import {get} from 'svelte/store';
import {cloneDeep} from 'lodash';
import type {ErrorMessagesLocalizationService} from '../errors/error-messages-localization.service';
import {isNullOrUndefined} from 'shared/utils/custom-utils';


/**
 * @ngdoc type
 * @name KpAsyncValidationOptions
 * @module portaro.value-editors.async-validation
 *
 * @property {boolean} sendWholeForm If `true` into validation function will be injectable whole wrapping form as `$formModel` argument.
 * @property {any} additionalParameters Any values passed into validation function as injectable `$additionalParameters` argument.
 * @property {{}} customFormModel if sendWholeForm is true then send this object instead of actual form model
 *
 * @description
 * Async validation parameters.
 */
export interface KpAsyncValidationOptions {
    sendWholeForm?: boolean;
    additionalParameters?: any;
    customFormModel?: AnyObject;
}

/**
 * @ngdoc constant
 * @name ASYNC_VALIDATION_DEFAULT_OPTIONS
 * @module portaro.value-editors.async-validation
 *
 * @description
 * Default async validation parameters value.
 *
 * ```
 *  {
 *      additionalParameters: undefined,
 *      sendWholeForm: false,
 *      customFormModel: undefined
 *  }
 * ```
 */
export const ASYNC_VALIDATION_DEFAULT_OPTIONS: KpAsyncValidationOptions = {
    additionalParameters: undefined,
    sendWholeForm: false,
    customFormModel: undefined
};

/**
 * @ngdoc type
 * @name AsyncValidationFunctionParams
 *
 * @property {string} fieldName Field name passed from `editorName` attribute of editor component
 * @property {unknown} value Current field value
 * @property {Record<string, unknown>} [form] If `sendWholeForm` {@link type:KpAsyncValidationOptions option} is true, it contains form model
 * @property {any} [additionalParameters] Some static parameters passed from `additionalParameters` in options.
 *
 * @description
 * Async validation function params.
 */
export interface AsyncValidationFunctionParams {
    fieldName: string;
    value: unknown;
    form?: Record<string, unknown>;
    additionalParameters?: any;
}

export type AsyncValidationFunction = ({fieldName, value, form, additionalParameters}: AsyncValidationFunctionParams) => Promise<boolean>;


export function asyncValidator(validationFunction: AsyncValidationFunction,
                               errorMessagesLocalizationService: ErrorMessagesLocalizationService,
                               fieldName: string,
                               formModel$: Readable<AnyObject>,
                               options: KpAsyncValidationOptions,
                               valueModifier?: <T>(_: T) => T | null): Validator {

    return async (value) => {
        const formModel = getFormModel(options, formModel$);
        let formModelWithUpdatedProperty;
        if (options.sendWholeForm && !isNullOrUndefined(formModel)) {
            formModelWithUpdatedProperty = updateCurrentPropertyToCurrentValue(formModel, fieldName, value, valueModifier);
        }
        try {
            const isValid = await validationFunction({
                fieldName,
                value,
                form: formModelWithUpdatedProperty,
                additionalParameters: options.additionalParameters
            });
            return {name: 'async', valid: isValid};
        } catch (errorMessage) {
            const errorMessageKey = `async-${fieldName}`;
            errorMessagesLocalizationService.setLocalization(errorMessageKey, errorMessage);
            return {name: errorMessageKey, valid: false};
        }
    }
}

function getFormModel(options: KpAsyncValidationOptions, formModel$: Readable<AnyObject>): AnyObject {
    if (options.customFormModel) {
        return options.customFormModel;
    }

    return cloneDeep(get(formModel$));
}

function updateCurrentPropertyToCurrentValue<MODEL extends Record<PROPERTY, any>, PROPERTY extends keyof MODEL>(model: MODEL, fieldName: PROPERTY, currentPropertyValue: MODEL[PROPERTY],  valueModifier?: (_: MODEL[PROPERTY]) => MODEL[PROPERTY]): MODEL {
    if (Object.hasOwn(model, fieldName)) {
        let value = currentPropertyValue;

        if (valueModifier) {
            value = valueModifier(value);
        }

        return {...model, [fieldName]: value};
    }

    return model;
}